import React, { useEffect, useState, useCallback } from "react";
import { getProgress, getDocumentDownloadUrl } from "../../api/adminApi";

function formatDateTime(value) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export default function DocumentProgressAdmin({ documentId, refreshKey = 0 }) {
  const [progress, setProgress] = useState(null);

  const load = useCallback(async () => {
    const res = await getProgress(documentId);
    setProgress(res.data);
  }, [documentId]);

  useEffect(() => {
    if (!documentId) return;
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [documentId, refreshKey, load]);

  if (!progress) return null;

  const isCompleted = progress.documentStatus === "SIGNED";
  const isRejected = progress.documentStatus === "REJECTED";
  const isExpired = progress.documentStatus === "EXPIRED";

  const rejectedSigner = (progress.signers || []).find(
    (signer) => signer.status === "REJECTED",
  );

  return (
    <div className="card">
      <div className="meta-list">
        <div className="meta-item">
          <div className="meta-label">Status</div>
          <div className="meta-value">
            <span
              className={`badge badge-${String(progress.documentStatus).toLowerCase()}`}
            >
              {progress.documentStatus}
            </span>
          </div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Signed Signers</div>
          <div className="meta-value">{progress.signedSigners}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Pending Signers</div>
          <div className="meta-value">{progress.pendingSigners}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Completed Fields</div>
          <div className="meta-value">
            {progress.completedFields} / {progress.totalFields}
          </div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Expires At</div>
          <div className="meta-value">{formatDateTime(progress.expiresAt)}</div>
        </div>
      </div>

      {progress.nextSigner && !isCompleted && !isRejected && !isExpired && (
        <div className="section-space info-banner">
          <strong>Next Signer:</strong> {progress.nextSigner.name} (
          {progress.nextSigner.email})
        </div>
      )}

      {isExpired && (
        <div className="warn-banner section-space">
          This document expired on {formatDateTime(progress.expiresAt)}.
        </div>
      )}

      {isRejected && rejectedSigner && (
        <div className="error-banner section-space">
          <div>
            <strong>Rejected By:</strong> {rejectedSigner.name} (
            {rejectedSigner.email})
          </div>
          <div>
            <strong>Reason:</strong>{" "}
            {rejectedSigner.rejectionReason || "No reason provided"}
          </div>
          <div>
            <strong>Rejected At:</strong>{" "}
            {formatDateTime(rejectedSigner.rejectedAt)}
          </div>
        </div>
      )}

      {(isCompleted || isRejected || isExpired) && (
        <div className="section-space">
          <a
            href={getDocumentDownloadUrl(documentId)}
            target="_blank"
            rel="noreferrer"
          >
            <button className="btn btn-primary">Download Current PDF</button>
          </a>
        </div>
      )}

      <div className="table-wrap section-space">
        <table className="table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Name</th>
              <th>Email</th>
              <th>Status</th>
              <th>OTP Verified</th>
              <th>Signed At</th>
              <th>Rejection Reason</th>
              <th>Rejected At</th>
            </tr>
          </thead>
          <tbody>
            {(progress.signers || []).map((signer) => (
              <tr key={signer.signerId}>
                <td>{signer.signOrder}</td>
                <td>{signer.name}</td>
                <td>{signer.email}</td>
                <td>
                  <span
                    className={`badge badge-${String(signer.status).toLowerCase()}`}
                  >
                    {signer.status}
                  </span>
                </td>
                <td>{signer.otpVerified ? "Yes" : "No"}</td>
                <td>{formatDateTime(signer.signedAt)}</td>
                <td>{signer.rejectionReason || "-"}</td>
                <td>{formatDateTime(signer.rejectedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
